import React, { useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import useBaseFile from './File.hook'
import { Browse, Input, NativeInput, Root } from './File.styles'

export const useFile = useBaseFile

/**
 * The File's component.
 */
const File = props => {
  const { accept, browseText, disabled, margin, padding, size, value, onChange, ...restProps } = props
  const nativeInputRef = useRef(null)
  const handleClick = useCallback(() => nativeInputRef.current.click(), [])
  const handleChange = useCallback(
    event => {
      const acceptTypes = accept.replace(/\s/g, '').split(',')
      const { accepted, rejected } = Array.from(event.target.files).reduce(
        (result, file) => {
          const hasValidType = acceptTypes.some(type => type === '*' || type === file.type)

          if (hasValidType) {
            return { ...result, accepted: result.accepted.concat(file) }
          }

          return { ...result, rejected: result.rejected.concat(file) }
        },
        { accepted: [], rejected: [] }
      )
      const isEmpty = event.target.files.length === 0
      const hasOnlyInvalidTypes = accepted.length === 0 && rejected.length > 0

      if (isEmpty || (hasOnlyInvalidTypes && Boolean(value))) {
        return
      }

      if (hasOnlyInvalidTypes && !value) {
        nativeInputRef.current.files = new DataTransfer().files
        nativeInputRef.current.value = ''

        return
      }

      onChange(accepted, rejected)
    },
    [accept, onChange, value]
  )

  useEffect(() => {
    if (value || !nativeInputRef.current) {
      return
    }

    nativeInputRef.current.files = new DataTransfer().files
    nativeInputRef.current.value = ''
  }, [value])

  return (
    <Root margin={margin} padding={padding}>
      <Input
        {...restProps}
        disabled
        fakeDisabled={disabled}
        size={size}
        suffix={
          <Browse disabled={disabled} size={size} type="button" onClick={handleClick}>
            {browseText}
          </Browse>
        }
        type="text"
        value={value ? `"${value?.name}"` : ''}
      />
      <NativeInput accept={accept} ref={nativeInputRef} type="file" onChange={handleChange} />
    </Root>
  )
}

File.defaultProps = {
  ...Input.defaultProps,
  accept: '*',
  browseText: 'Examinar',
  size: 'medium'
}

File.propTypes = {
  ...Input.propTypes,
  /**
   * Accept.
   */
  accept: PropTypes.string,
  /**
   * Autofocus.
   */
  autoFocus: PropTypes.bool,
  /**
   * Browse text.
   */
  browseText: PropTypes.string,
  /**
   * Disabled.
   */
  disabled: PropTypes.bool,
  /**
   * Error.
   */
  error: PropTypes.string,
  /**
   * Info.
   */
  info: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * Label.
   */
  label: PropTypes.string,
  /**
   * Loading.
   */
  loading: PropTypes.bool,
  /**
   * Margin.
   */
  margin: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Padding.
   */
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /**
   * Placeholder.
   */
  placeholder: PropTypes.string,
  /**
   * Required.
   */
  required: PropTypes.bool,
  /**
   * Size.
   */
  size: PropTypes.oneOf(['large', 'medium', 'small']),
  /**
   * Success.
   */
  success: PropTypes.bool,
  /**
   * Tooltip.
   */
  tooltip: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  /**
   * Value.
   */
  value: PropTypes.any,
  /**
   * On change.
   */
  onChange: PropTypes.func
}

export default File
